// 交易详情字段配置
// 根据四种业务类型分别定义不同的字段列表：移动支付类、银行卡支付、VISA分期、外部渠道流水

interface DetailItem {
  label: string;
  value: string;
}

interface DetailGroup {
  title: string;
  exList: DetailItem[];
}

/**
 * 交易详情页-移动支付类
 * 交易主体：商户名称、商户标识、终端标识、厂商、型号、设备S/N
 * 交易信息：网关接收交易时间、商户交易单号、原商户交易单号、交易名称、终端发起流水号、终端发起时间、币种、交易金额
 * 交易结果：状态、描述
 * 支付信息：支付渠道、渠道商户号、渠道终端号、支付时间、支付订单号、优惠信息、实付币种、支付者标识、支付者名称
 */
const mobilePaymentDetails: DetailGroup[] = [
  {
    title: 'Transaction Parties',
    exList: [
      { label: '商户名称', value: 'merName' },
      { label: '商户标识', value: 'mid' },
      { label: '终端标识', value: 'tid' },
      { label: '厂商', value: 'manuName' },
      { label: '型号', value: 'modelName' },
      { label: '设备S/N', value: 'sn' },
    ],
  },
  {
    title: 'Transaction Information',
    exList: [
      { label: '网关接收交易时间', value: 'sysDateTime' },
      { label: '商户交易单号', value: 'sysId' },
      { label: '原商户交易单号', value: 'oriSysId' },
      { label: '交易名称', value: 'transName' },
      { label: '终端发起流水号', value: 'traceNo' },
      { label: '终端发起时间', value: 'termDateTime' },
      { label: '币种', value: 'currency' },
      { label: '交易金额', value: 'amt' },
    ],
  },
  {
    title: 'Transaction Result',
    exList: [
      { label: '状态', value: 'orderStatus' },
      { label: '描述', value: 'errorMsg' },
    ],
  },
  {
    title: 'Payment Information',
    exList: [
      { label: '支付渠道', value: 'channelName' },
      { label: '渠道商户号', value: 'outMid' },
      { label: '渠道终端号', value: 'outTid' },
      { label: '支付时间', value: 'payDateTime' },
      { label: '支付订单号', value: 'authNo' },
      { label: '优惠信息', value: 'testfont1' },
      { label: '实付币种', value: 'actualPaymentCurrency' },
      { label: '实付金额', value: 'testfont2' },
      { label: '汇率', value: 'rate' },
      { label: '支付者标识', value: 'userId' },
      { label: '支付者名称', value: 'userLoginInfo' },
    ],
  },
];

/**
 * 交易详情页-银行卡支付
 * 交易主体：商户名称、商户标识、终端标识
 * 交易信息：网关接收交易时间、商户交易单号、原商户交易单号、交易名称、终端发起流水号、终端发起时间、币种、交易金额
 * 交易结果：状态、描述
 * 支付信息：支付渠道、渠道商户号、渠道终端号、支付时间、支付订单号、卡号、卡品牌、卡类型、发卡机构
 */
const bankCardDetails: DetailGroup[] = [
  {
    title: 'Transaction Parties',
    exList: [
      { label: '商户名称', value: 'merName' },
      { label: '商户标识', value: 'mid' },
      { label: '终端标识', value: 'tid' },
    ],
  },
  {
    title: 'Transaction Information',
    exList: [
      { label: '网关接收交易时间', value: 'sysDateTime' },
      { label: '商户交易单号', value: 'sysId' },
      { label: '原商户交易单号', value: 'oriSysId' },
      { label: '交易名称', value: 'transName' },
      { label: '终端发起流水号', value: 'traceNo' },
      { label: '终端发起时间', value: 'termDateTime' },
      { label: '币种', value: 'currency' },
      { label: '交易金额', value: 'amt' },
    ],
  },
  {
    title: 'Transaction Result',
    exList: [
      { label: '状态', value: 'orderStatus' },
      { label: '描述', value: 'errorMsg' },
    ],
  },
  {
    title: 'Payment Information',
    exList: [
      { label: '支付渠道', value: 'channelName' },
      { label: '渠道商户号', value: 'outMid' },
      { label: '渠道终端号', value: 'outTid' },
      { label: '支付时间', value: 'payDateTime' },
      { label: '支付订单号', value: 'authNo' },
      { label: '卡号', value: 'accountHolder' },
      { label: '卡品牌', value: 'accountHolderBankType' },
      { label: '卡类型', value: 'accountHolderType' },
      { label: '发卡机构', value: 'accountHolderIssuer' },
    ],
  },
];

/**
 * 交易详情页-VISA分期
 * 交易主体：商户名称、商户标识、终端标识、厂商、型号、设备S/N
 * 交易信息：网关接收交易时间、商户交易单号、原商户交易单号、交易名称、终端发起流水号、终端发起时间、币种、交易金额
 * 交易结果：状态、描述
 * 支付信息：支付渠道、渠道商户号、渠道终端号、支付时间、支付订单号、分期相关字段
 */
const visaInstallmentDetails: DetailGroup[] = [
  {
    title: 'Transaction Parties',
    exList: [
      { label: '商户名称', value: 'merName' },
      { label: '商户标识', value: 'mid' },
      { label: '终端标识', value: 'tid' },
      { label: '厂商', value: 'manuName' },
      { label: '型号', value: 'modelName' },
      { label: '设备S/N', value: 'sn' },
    ],
  },
  {
    title: 'Transaction Information',
    exList: [
      { label: '网关接收交易时间', value: 'sysDateTime' },
      { label: '商户交易单号', value: 'sysId' },
      { label: '原商户交易单号', value: 'oriSysId' },
      { label: '交易名称', value: 'transName' },
      { label: '终端发起流水号', value: 'traceNo' },
      { label: '终端发起时间', value: 'termDateTime' },
      { label: '币种', value: 'currency' },
      { label: '交易金额', value: 'amt' },
    ],
  },
  {
    title: 'Transaction Result',
    exList: [
      { label: '状态', value: 'orderStatus' },
      { label: '描述', value: 'errorMsg' },
    ],
  },
  {
    title: 'Payment Information',
    exList: [
      { label: '支付渠道', value: 'channelName' },
      { label: '渠道商户号', value: 'outMid' },
      { label: '渠道终端号', value: 'outTid' },
      { label: '支付时间', value: 'payDateTime' },
      { label: '支付订单号', value: 'authNo' },
      { label: '分期期数', value: 'numberOfInstallments' },
      { label: '还款周期', value: 'installmentFrequency' },
      { label: '分期总额', value: 'totalPlanCost' },
      { label: '分期总手续费', value: 'totalFees' },
      { label: '首期本金', value: 'Famount' },
      { label: '首期手续费', value: 'FinstallmentFee' },
      { label: '末期本金', value: 'Lamount' },
      { label: '末期手续费', value: 'LinstallmentFee' },
      { label: 'vPlanRef', value: 'vplanRef' },
      { label: 'externalTransactionRef', value: 'externalTransactionRef' },
      { label: 'vPlanID', value: 'vplanID' },
      { label: 'vPlanAcceptanceId', value: 'vplanAcceptanceId' },
    ],
  },
];

/**
 * 交易详情页-外部渠道流水
 * 交易主体：商户名称、商户标识、终端标识、厂商、型号、设备S/N
 * 交易信息：网关接收交易时间、商户交易单号、原商户交易单号、交易名称、终端发起流水号、终端发起时间、币种、交易金额
 * 交易结果：状态、描述
 * 支付信息：支付渠道、渠道商户号、渠道终端号、汇率、支付订单号、币种、交易金额
 */
const externalChannelDetails: DetailGroup[] = [
  {
    title: 'Transaction Parties',
    exList: [
      { label: '商户名称', value: 'merName' },
      { label: '商户标识', value: 'mid' },
      { label: '终端标识', value: 'tid' },
      { label: '厂商', value: 'manuName' },
      { label: '型号', value: 'modelName' },
      { label: '设备S/N', value: 'sn' },
    ],
  },
  {
    title: 'Transaction Information',
    exList: [
      { label: '网关接收交易时间', value: 'sysDateTime' },
      { label: '商户交易单号', value: 'sysId' },
      { label: '原商户交易单号', value: 'oriSysId' },
      { label: '交易名称', value: 'transName' },
      { label: '终端发起流水号', value: 'traceNo' },
      { label: '终端发起时间', value: 'termDateTime' },
      { label: '币种', value: 'currency' },
      { label: '交易金额', value: 'amt' },
    ],
  },
  {
    title: 'Transaction Result',
    exList: [
      { label: '状态', value: 'orderStatus' },
      { label: '描述', value: 'errorMsg' },
    ],
  },
  {
    title: 'Payment Information',
    exList: [
      { label: '支付渠道', value: 'channelName' },
      { label: '渠道商户号', value: 'outMid' },
      { label: '渠道终端号', value: 'outTid' },
      { label: '汇率', value: 'rate' },
      { label: '支付订单号', value: 'authNo' },
      { label: '币种', value: 'currency' },
      { label: '交易金额', value: 'amt' },
    ],
  },
];

/**
 * 根据业务类型获取对应的详情字段列表
 * @param activeAbil 当前业务类型：移动支付类 | 银行卡支付 | VISA分期 | 外部渠道流水
 */
export function getDetailsByAbil(activeAbil: string): DetailGroup[] {
  switch (activeAbil) {
    case '银行卡支付':
      return bankCardDetails;
    case 'VISA分期':
      return visaInstallmentDetails;
    case '外部渠道流水':
      return externalChannelDetails;
    case '移动支付类':
    default:
      return mobilePaymentDetails;
  }
}

/**
 * 兼容旧接口 - 返回默认的移动支付类字段列表
 * 用于不区分业务类型的场景（向后兼容）
 */
export function useDetailsData() {
  // 默认返回移动支付类字段列表（向后兼容）
  const detailsList = mobilePaymentDetails;

  return {
    detailsList,
    // 导出所有类型的字段列表，方便按需使用
    mobilePaymentDetails,
    bankCardDetails,
    visaInstallmentDetails,
    externalChannelDetails,
    getDetailsByAbil,
  };
}
